import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { supabase } from "../../lib/supabaseClient";
import { JUNGNA_STORAGE, JUNGNA_TABLES } from "../../lib/jungnaTables";

export interface ProductImageItem {
  id: string;
  storage_path: string;
  public_url: string | null;
  position: number;
}

interface MultiImageUploaderProps {
  productId: string;
  images: ProductImageItem[];
  onRefresh: () => Promise<void> | void;
  onSetPrimary?: (url: string) => Promise<void> | void;
}

const MultiImageUploader: React.FC<MultiImageUploaderProps> = ({ productId, images, onRefresh, onSetPrimary }) => {
  const [isUploading, setIsUploading] = useState(false);

  const sortedImages = useMemo(() => {
    return [...images].sort((a, b) => a.position - b.position || 0);
  }, [images]);

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0 || !productId) return;
    setIsUploading(true);
    try {
      const startPosition = images.length;
      const uploadedUrls = await Promise.all(
        Array.from(files).map(async (file, idx) => {
          const path = `${productId}/${Date.now()}-${idx}-${file.name}`;
          const { error: uploadError } = await supabase.storage
            .from(JUNGNA_STORAGE.productImages)
            .upload(path, file, { upsert: false, cacheControl: "3600" });
          if (uploadError) throw uploadError;

          const { data: publicData } = supabase.storage
            .from(JUNGNA_STORAGE.productImages)
            .getPublicUrl(path);
          const publicUrl = publicData?.publicUrl || null;

          const { error: insertError } = await supabase
            .from(JUNGNA_TABLES.productImages)
            .insert({
              product_id: productId,
              storage_path: path,
              public_url: publicUrl,
              position: startPosition + idx,
            });
          if (insertError) throw insertError;
          return publicUrl;
        })
      );
      await onRefresh();

      // 첫 업로드시 대표 이미지 설정
      if (onSetPrimary && images.length === 0 && uploadedUrls[0]) {
        await onSetPrimary(uploadedUrls[0]);
      }
    } catch (e) {
      console.error("이미지 업로드 실패:", e);
      alert("이미지 업로드에 실패했어. 다시 시도해줘!");
    } finally {
      setIsUploading(false);
    }
  }, [productId, images.length, onRefresh, onSetPrimary]);

  const handleDelete = useCallback(async (item: ProductImageItem) => {
    if (!window.confirm("이 이미지를 삭제할까?")) return;
    try {
      const { error: delDbError } = await supabase.from(JUNGNA_TABLES.productImages).delete().eq("id", item.id);
      if (delDbError) throw delDbError;
      await supabase.storage.from(JUNGNA_STORAGE.productImages).remove([item.storage_path]);
      await onRefresh();
    } catch (e) {
      console.error("이미지 삭제 실패:", e);
      alert("이미지 삭제 실패… 잠시 후 다시 시도해줘");
    }
  }, [onRefresh]);

  const setPrimary = useCallback(async (item: ProductImageItem) => {
    if (!onSetPrimary) return;
    const url = item.public_url ?? supabase.storage.from(JUNGNA_STORAGE.productImages).getPublicUrl(item.storage_path).data.publicUrl;
    await onSetPrimary(url);
    alert("대표 이미지로 설정됐어!");
  }, [onSetPrimary]);

  return (
    <Container>
      <Label>상품 이미지 업로드</Label>
      <Input type="file" accept="image/*" multiple onChange={(e) => handleFiles(e.target.files)} disabled={isUploading} />
      {isUploading && <Hint>업로드 중... 잠시만!</Hint>}
      <Grid>
        {sortedImages.map((img) => {
          const url = img.public_url ?? supabase.storage.from(JUNGNA_STORAGE.productImages).getPublicUrl(img.storage_path).data.publicUrl;
          return (
            <Card key={img.id}>
              <Thumb src={url} alt="product" />
              <Actions>
                <SmallButton onClick={() => setPrimary(img)}>대표</SmallButton>
                <DangerButton onClick={() => handleDelete(img)}>삭제</DangerButton>
              </Actions>
            </Card>
          );
        })}
      </Grid>
    </Container>
  );
};

const Container = styled.div``;
const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #495057;
  font-size: 15px;
`;
const Input = styled.input``;
const Hint = styled.div`
  margin-top: 8px;
  color: #6c757d;
  font-size: 13px;
`;
const Grid = styled.div`
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 12px;
`;
const Card = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Thumb = styled.img`
  width: 100%;
  height: 96px;
  object-fit: cover;
  border-radius: 6px;
`;
const Actions = styled.div`
  display: flex;
  gap: 6px;
  justify-content: space-between;
`;
const SmallButton = styled.button`
  flex: 1;
  padding: 6px 8px;
  font-size: 12px;
  border: none;
  border-radius: 6px;
  background: #0d6efd;
  color: #fff;
  cursor: pointer;
`;
const DangerButton = styled(SmallButton)`
  background: #dc3545;
`;

export default MultiImageUploader;
